import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import {
    ActionList, ActionRow, AppScreen, FieldGroup, Section, SectionHeader, TextField,
} from 'components/ds';
import { IcUser, IcCamera } from 'components/common/icons/EmojiIcons';
import { decodeJwtPayload } from 'common/utils/auth';
import { getUserInfo, saveUserInfo } from 'common/utils/secureStorage';
import { UserRepository } from 'repository/userRepository';
import { userStore } from 'store/userStore';
import { isNativeApp } from 'common/webview';
import { pickPhotoFile, shouldTellUser } from 'common/utils/photoPick';
import styles from './Profile.module.css';

/** JWT에서 provider 추출 — base64url + UTF-8 decode 안전 처리 */
const getProviderFromToken = (): string => {
    try {
        const token = localStorage.getItem('accessToken');
        if (!token) return 'EMAIL';
        const payload = decodeJwtPayload(token);
        return payload?.provider || 'EMAIL';
    } catch {
        return 'EMAIL';
    }
};

export const Profile: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showAlert, showConfirm } = useAlert();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [nickname, setNickname] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [email, setEmail] = useState('');
    const [profileImgPath, setProfileImgPath] = useState('');
    /* 사진을 고르거나 올리는 중 — 이 동안 저장·사진 변경을 막습니다.
       저장하면 임시 미리보기 주소(blob:)가 secureStorage 와 앱(SAVE_USER_INFO)에 남고,
       업로드가 끝나 그 주소가 해제되면 다른 화면의 프로필 사진이 깨집니다.
       state 는 다음 렌더에야 반영되므로 연타는 ref 로 막습니다. */
    const [imageBusy, setImageBusy] = useState(false);
    const imageBusyRef = useRef(false);
    const [provider] = useState(getProviderFromToken());

    const isEmailUser = provider === 'EMAIL';

    useEffect(() => {
        const cached = getUserInfo();
        if (cached) {
            setNickname(cached.nickname || '');
            setProfileImgPath(cached.profileImgPath || '');
        }

        const fetchProfile = async () => {
            try {
                const accessToken = localStorage.getItem('accessToken');
                if (!accessToken) {
                    setLoading(false);
                    return;
                }

                // JWT.sub 에서 암호화된 userId 추출 — /auth/user 쿼리로 전달해
                // Mock 모드 기본 분기 회피 (실제 내 유저 정보 조회 보장)
                let encryptedUserId: string | undefined;
                try {
                    const payload = decodeJwtPayload(accessToken);
                    if (typeof payload?.sub === 'string') encryptedUserId = payload.sub;
                } catch { /* ignore */ }

                const response = await UserRepository.onGetSsoUser(accessToken, encryptedUserId);
                if (response?.data?.data) {
                    const data = response.data.data;
                    const rawNickname = data.nickName || data.nickname || '';
                    let decodedNickname = rawNickname;
                    try { decodedNickname = decodeURIComponent(rawNickname); } catch { /* already decoded */ }

                    setNickname(decodedNickname);
                    setEmail(data.email || data.maskedEmail || '');
                    setStatusMessage(data.statusMessage || '');
                    setProfileImgPath(data.profileImgPath || '');

                    userStore.onSaveUserInfo(data);
                }
            } catch (error) {
                console.error('프로필 조회 실패:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    /* 고른 파일을 미리 보여 주고 서버에 올립니다 — 앱·웹 공통.
       예전에는 앱에서 고른 사진을 올리지 않고 앱 캐시 경로(file://)만 화면에 넣었습니다.
       웹뷰는 그 경로를 읽지 못해 미리보기가 비었고, 저장을 누르면 그 경로가 캐시에 남았습니다.
       미리보기는 objectURL 로 즉시 겁니다 — FileReader 는 비동기라 업로드가 먼저 끝나면
       서버 주소를 data URL 로 덮어써, 저장 시 거대한 문자열이 캐시에 들어갑니다. */
    const uploadProfileImage = async (file: File) => {
        const previous = profileImgPath;
        const preview = URL.createObjectURL(file);
        setProfileImgPath(preview);

        try {
            const uploadRes = await UserRepository.onUploadProfileImage(file);
            const uploadData = uploadRes?.data?.data || uploadRes?.data;
            // 서버는 { fileId, fileUrl, ... } 를 주고 계정의 profileImgPath 도 그 주소로 바꿉니다
            const newUrl = uploadData?.fileUrl || uploadData?.profileImgPath || uploadData?.url;
            if (!newUrl) throw new Error('no image url in response');
            setProfileImgPath(newUrl);

            // secureStorage 갱신
            const current = getUserInfo();
            if (current) {
                saveUserInfo({ ...current, profileImgPath: newUrl });
            }
            await showAlert({ message: t('profile.alertImgChanged') });
        } catch {
            // 되돌려야 저장 버튼이 올라가지 않은 사진 주소를 캐시에 남기지 않습니다
            setProfileImgPath(previous);
            await showAlert({ message: t('profile.alertImgFail') });
        } finally {
            URL.revokeObjectURL(preview);
        }
    };

    const runImageTask = async (task: () => Promise<void>) => {
        if (imageBusyRef.current) return;
        imageBusyRef.current = true;
        setImageBusy(true);
        try {
            await task();
        } finally {
            imageBusyRef.current = false;
            setImageBusy(false);
        }
    };

    // 프로필 이미지 변경 + 업로드
    const handleChangeImage = () => {
        if (imageBusyRef.current) return;
        if (isNativeApp()) {
            // 네이티브 갤러리 — 앱이 준 base64 를 파일로 바꿔 받습니다
            runImageTask(async () => {
                const picked = await pickPhotoFile();
                if (picked.ok) {
                    await uploadProfileImage(picked.file);
                } else if (shouldTellUser(picked.reason)) {
                    await showAlert({ message: t('profile.alertImgFail') });
                }
            });
        } else {
            // 웹: file input으로 선택 → 바로 업로드.
            // 파일 창을 그냥 닫으면 이벤트가 오지 않으므로, 막는 것은 파일을 고른 뒤부터입니다.
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (!file) return;
                runImageTask(() => uploadProfileImage(file));
            };
            input.click();
        }
    };

    const handleSave = async () => {
        if (!nickname.trim()) {
            await showAlert({ message: t('profile.alertNickname') });
            return;
        }

        setSaving(true);
        try {
            // 닉네임 변경
            const nicknameRes = await UserRepository.onUpdateNickname(nickname.trim());
            if (nicknameRes?.status !== 200 && nicknameRes?.status !== 201) {
                const msg = nicknameRes?.data?.message || t('profile.alertNicknameFail');
                await showAlert({ message: msg });
                setSaving(false);
                return;
            }

            // 상태 메시지 변경
            await UserRepository.onUpdateStatusMsg(statusMessage.trim() || null);

            // 로컬 캐시 갱신
            const current = getUserInfo();
            if (current) {
                saveUserInfo({
                    ...current,
                    nickname: nickname.trim(),
                    profileImgPath,
                    statusMessage: statusMessage.trim(),
                });
                sessionStorage.setItem('userName', nickname.trim());
            }

            await showAlert({ message: t('profile.alertSaved') });
            navigate(-1);
        } catch (error) {
            console.error('프로필 저장 실패:', error);
            await showAlert({ message: t('profile.alertSaveError') });
        } finally {
            setSaving(false);
        }
    };

    const handleWithdraw = async () => {
        const confirmed = await showConfirm({
            message: t('profile.withdrawConfirm'),
            confirmText: t('profile.withdrawConfirmBtn')
        });

        if (confirmed) {
            try {
                const response = await UserRepository.onWithdraw();
                if (response?.status === 200 || response?.status === 204) {
                    userStore.onLogout();
                    // 네이티브 환경 잔여 atom/메모리까지 깨끗이 — handleLogout 과 동일 패턴
                    if (window.ReactNativeWebView) {
                        window.location.replace('/login');
                    } else {
                        navigate('/login', { replace: true });
                    }
                } else {
                    await showAlert({ message: t('profile.alertWithdrawFail') });
                }
            } catch (error) {
                console.error('회원탈퇴 실패:', error);
                await showAlert({ message: t('profile.alertWithdrawError') });
            }
        }
    };

    const handleChangePassword = () => {
        navigate('/settings/change-password');
    };

    if (loading) {
        return (
            <AppScreen title={t('profile.title')} bar="sub" onBack={() => navigate('/settings')}>
                <p className={styles.state}>{t('profile.loading')}</p>
            </AppScreen>
        );
    }

    return (
        <AppScreen
            title={t('profile.title')}
            bar="sub"
            onBack={() => navigate('/settings')}
            right={
                <button type="button" className={styles.save} onClick={handleSave} disabled={saving || imageBusy}>
                    {saving ? t('profile.saving') : t('profile.save')}
                </button>
            }
        >
            {/* 프로필 사진 */}
            <div className={styles.avatarBlock}>
                <div className={styles.avatar}>
                    {profileImgPath
                        ? <img src={profileImgPath} alt="" />
                        : <IcUser size={40} />}
                    <button
                        type="button"
                        className={styles.avatarChange}
                        onClick={handleChangeImage}
                        disabled={imageBusy}
                        aria-label={t('profile.changeImage')}
                    >
                        <IcCamera size={16} />
                    </button>
                </div>
                <span className={styles.name}>{nickname}</span>
                {email ? <span className={styles.email}>{email}</span> : null}
            </div>

            {/* 기본 정보 */}
            <Section spacing="tight">
                <SectionHeader title={t('profile.basicInfo')} />
                <FieldGroup>
                    <TextField
                        id="nickname"
                        label={t('profile.nickname')}
                        type="text"
                        value={nickname}
                        onChange={(e) => setNickname(e.target.value)}
                        maxLength={20}
                    />
                    <TextField
                        id="statusMessage"
                        label={t('profile.statusMessage')}
                        type="text"
                        value={statusMessage}
                        onChange={(e) => setStatusMessage(e.target.value)}
                        maxLength={60}
                    />
                </FieldGroup>
            </Section>

            {/* 계정 정보 */}
            <Section divided last>
                <SectionHeader title={t('profile.accountInfo')} />
                {email ? (
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>{t('profile.email')}</span>
                        <span className={styles.infoValue}>{email}</span>
                    </div>
                ) : null}
                <ActionList>
                    {isEmailUser && (
                        <ActionRow
                            title={t('profile.changePassword')}
                            onClick={handleChangePassword}
                        />
                    )}
                    <ActionRow
                        title={t('profile.withdraw')}
                        onClick={handleWithdraw}
                        danger
                        noArrow
                    />
                </ActionList>
            </Section>
        </AppScreen>
    );
};
